import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from '@googlemaps/markerclusterer';
import { GeoLocation, MapData } from '../type';
import { useStore } from 'zustand';
import { useMapStore } from '@/stores/map/mapStore';
import { MAP_INITIAL_STATE } from '@/utils/constants';
import { InfoWindowContent } from './google-maps/info-window-content';
import ReactDOMServer from 'react-dom/server';

interface Props {
  map: google.maps.Map | null;
}

interface MappingData {
  markers: google.maps.marker.AdvancedMarkerElement;
  locations: GeoLocation;
}

export const ClusteredMarkers: React.FC<Props> = ({ map }) => {
  const {
    data,
    clientLocation,
    hasSetLocation,
    activeMarker,
    setActiveMarker,
  } = useStore(useMapStore, (state) => ({
    data: state.data,
    clientLocation: state.clientLocation,
    hasSetLocation: state.hasSetLocation,
    activeMarker: state.activeMarker,
    setActiveMarker: state.setActiveMarker,
  }));

  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const [loadedMarkers, setLoadedMarkers] = useState<MappingData[]>([]);
  const mapMarkerData: GeoLocation[] = useMemo(
    () =>
      data.radialData.inside.map((item) => ({
        id: item.id,
        lat: item.attributes.latitude,
        lng: item.attributes.longtitude,
      })),
    [data.radialData.inside]
  );

  const initMap = async (): Promise<MappingData[]> => {
    const addCustomMarker = (
      coords: GeoLocation,
      content: HTMLDivElement | HTMLElement
    ) => {
      return new google.maps.marker.AdvancedMarkerElement({
        position: coords,
        content,
      });
    };

    const generateClusterMarkers = (
      data: MapData[],
      markerData: {
        glyphColor: string;
        borderColor: string;
        background: string;
      }
    ): MappingData[] => {
      return data.map((marker) => {
        const attributes = marker.attributes;
        const pinGlyph = new google.maps.marker.PinElement(markerData);
        const position: GeoLocation = {
          id: marker.id,
          lat: attributes.latitude,
          lng: attributes.longtitude,
        };
        const mapPin = addCustomMarker(position, pinGlyph.element);

        infoWindowRef.current = new google.maps.InfoWindow({
          content: '',
          disableAutoPan: true,
        });

        mapPin.addListener('gmp-click', () => {
          const markerInfo = {
            id: marker.id,
            position,
            label: String(attributes.title),
            info: attributes,
            isDispatch: !!!marker.dispatch,
            clickThruUrl: undefined,
            price: undefined,
            lowStock: undefined,
            outOfStock: undefined,
          };

          setActiveMarker(markerInfo);

          const infoWindowContent = ReactDOMServer.renderToString(
            <InfoWindowContent
              data={{
                title: attributes.title,
                address: attributes.address,
                phone: attributes.phone,
                email: attributes.email,
                description: attributes.description,
              }}
            />
          );
          infoWindowRef.current?.setContent(infoWindowContent);
          infoWindowRef.current?.open(map, mapPin);
        });

        return { markers: mapPin, locations: position };
      });
    };

    clustererRef.current?.clearMarkers();

    const insideMarkers = generateClusterMarkers(data.radialData.inside, {
      glyphColor: '#FFFFFF',
      borderColor: '#0071E3',
      background: '#0071E3',
    });

    clustererRef.current = new MarkerClusterer({
      algorithm: new SuperClusterAlgorithm({ maxZoom: 10, radius: 80 }),
      map,
      markers: [...insideMarkers.map((marker) => marker.markers)],
    });

    if (hasSetLocation()) {
      const userPinGlyph = new google.maps.marker.PinElement({
        glyphColor: '#FFFFFF',
        borderColor: '#FF0000',
        background: '#FF0000',
      });

      clientLocation.location &&
        addCustomMarker(clientLocation.location, userPinGlyph.element);
    }

    const mrcoolHQSVG = `
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
        <circle cx="20" cy="20" r="18" fill="#FF5733" stroke="#333" stroke-width="2" />
        <text x="20" y="25" font-size="14" font-family="Arial" fill="#FFF" text-anchor="middle">
          M
        </text>
      </svg>
    `;
    const customMarkerDiv = document.createElement('div');
    customMarkerDiv.innerHTML = mrcoolHQSVG;

    addCustomMarker(MAP_INITIAL_STATE.mapCenter.geometry, customMarkerDiv);

    return insideMarkers;
  };

  useEffect(() => {
    const selectedId = activeMarker?.id as number;
    const selectedMarkerData = loadedMarkers.find(
      ({ locations }) => locations.id === selectedId
    );

    if (!selectedMarkerData) return;

    const markerAttributes =
      activeMarker?.id &&
      data.radialData.inside.find((item) => item.id === activeMarker.id)
        ?.attributes;

    if (!markerAttributes) return;

    const infoString = ReactDOMServer.renderToString(
      <InfoWindowContent
        data={{
          title: markerAttributes?.title,
          address: markerAttributes?.address,
          phone: markerAttributes?.phone,
          email: markerAttributes?.email,
          description: markerAttributes?.description,
        }}
      />
    );

    infoWindowRef.current?.setContent(infoString);
    infoWindowRef.current?.open(map, selectedMarkerData.markers);

    return () => {};
  }, [activeMarker]);

  useEffect(() => {
    const loadMarkers = async () => {
      const loadedMarkers = await initMap();

      setLoadedMarkers(loadedMarkers);
    };

    loadMarkers();

    return () => {
      clustererRef.current?.clearMarkers();
    };
  }, [mapMarkerData]);

  return null;
};
